import { useEffect, useRef, useState } from "react";
import type { PlaylistItem, ScreenDefaultItem } from "../api/types";
import { config } from "../config";
import { usePlayerStore } from "../state/playerStore";
import { addPop } from "../sync/popBuffer";
import { resolvePlayableUrl } from "./mediaCache";
import { playChanged, popForEndedPlay, type ResolvedPlay, resolvePlay } from "./scheduler";

type Renderable = PlaylistItem | ScreenDefaultItem;

// Fullscreen, clock-driven renderer. Every tick it derives the current slot from
// the server-adjusted wall clock. A contracted slot item plays only on its
// throttled share of cycles; every other slot is filled by the bonus rotation
// over the whole playlist. Every slot boundary (or mid-slot media change)
// records proof-of-play for the play that just ended — contracted plays against
// their booking_slot index, bonus fills against the cycle slot they occupied.

export function Player() {
  const screen = usePlayerStore((s) => s.screen);
  const online = usePlayerStore((s) => s.online);
  const [item, setItem] = useState<Renderable | null>(null);
  // Slot start of the current play — part of the video element's key so the
  // creative RESTARTS every slot, even when the same media fills consecutive
  // slots (single-item playlist, duplicate media in adjacent slots).
  // Keying on media_id alone would leave the element mounted: play once, freeze.
  const [playStartMs, setPlayStartMs] = useState(0);
  const [url, setUrl] = useState<string | null>(null);

  const playRef = useRef<ResolvedPlay | null>(null);
  const startRef = useRef<number>(0);

  useEffect(() => {
    const tick = () => {
      const st = usePlayerStore.getState();
      // Tick whenever the screen is known — INCLUDING when there is nothing to
      // show. Returning early on empty content would freeze the previous play:
      // when the sync empties the playlist (no bookings, no screen default),
      // the boundary below would never run — the last creative would keep
      // looping, its final proof-of-play would never be emitted, and the
      // heartbeat would keep reporting the stale current play. Idle is a play
      // state, not a reason to stop ticking.
      if (!st.screen) return;
      const now = Date.now() + st.serverOffsetMs;
      const play = resolvePlay(
        now,
        st.playlist,
        st.screen.slots_per_cycle,
        st.screen.slot_duration_seconds,
        st.screenDefault,
      );
      const prev = playRef.current;
      // Boundary = slot tick or mid-slot media change; a media_id comparison
      // alone would collapse consecutive plays of the same creative.
      if (!playChanged(prev, play)) return;

      // Idle → idle slot boundaries carry no work: nothing ended (no PoP), the
      // reported state is already "idle", and re-rendering "Waiting for
      // content…" every slot is churn. Keep the refs current and stop.
      if (prev !== null && prev.media === null && play.media === null) {
        playRef.current = play;
        startRef.current = play.position.slotStartMs;
        return;
      }

      const record = popForEndedPlay(prev, startRef.current, now);
      if (record) void addPop(record);

      playRef.current = play;
      startRef.current = play.position.slotStartMs;
      // Full render report (heartbeat ground truth): what plays, on which
      // channel. media null → "idle"; the screen default has no booking.
      st.setCurrentPlay({
        slotIndex: play.position.slotIndex,
        channel: play.media === null ? "idle" : play.channel,
        bookingId:
          play.media !== null && "booking_id" in play.media ? play.media.booking_id : null,
        mediaId: play.media?.media_id ?? null,
      });
      setItem(play.media);
      setPlayStartMs(play.position.slotStartMs);
    };

    tick();
    const id = setInterval(tick, config.playbackTickMs);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!item) {
      setUrl(null);
      return;
    }
    resolvePlayableUrl(item).then((u) => {
      if (!cancelled) setUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [item]);

  if (!screen || !item) {
    return (
      <div className="fill center idle">
        <span className="idle-text">Waiting for content…</span>
      </div>
    );
  }

  return (
    <div className="fill black">
      {url &&
        (item.media_type === "video" ? (
          // Keyed per play instance so each slot restarts the creative; `loop`
          // fills the remainder of the slot when the video is shorter than it.
          <video
            key={`${item.media_id}-${playStartMs}`}
            className="fill media"
            src={url}
            autoPlay
            muted
            playsInline
            loop
          />
        ) : (
          <img key={item.media_id} className="fill media" src={url} alt="" />
        ))}
      {!online && <div className="offline-badge">offline · playing from cache</div>}
    </div>
  );
}
